import { ReactNode, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useMediaQuery } from "@/zapi-frontend/shared/hooks/useMediaQuery";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { ChatFab } from "./ChatFab";

// Docked chat width scales with the browser window: 340px at ≤1512px, 364px in between, 400px at ≥1920px
export const chatPanelWidthClasses = "w-[340px] min-[1513px]:w-[364px] min-[1920px]:w-[400px]";

// Same INV-2223 scale for panels that only dock at `hd` and stay a full-width overlay below it.
export const dockedChatPanelWidthClasses =
  "hd:w-[340px] hd:max-w-[340px] min-[1513px]:w-[364px] min-[1513px]:max-w-[364px] min-[1920px]:w-[400px] min-[1920px]:max-w-[400px]";

type TResponsiveChatPanelProps = {
  children: ReactNode;
  hasUnread?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Mount the overlay's children immediately instead of waiting for the first
   * open. Needed when the chat itself must run while collapsed (e.g. to emit an
   * intro message that raises the unread dot before the user opens it).
   */
  eagerMount?: boolean;
};

export function ResponsiveChatPanel({
  children,
  hasUnread = false,
  onOpenChange,
  eagerMount = false
}: TResponsiveChatPanelProps) {
  const isDocked = useMediaQuery("(min-width: 984px)");
  const [isOpen, setIsOpen] = useState(false);
  const hasOpened = useRef(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") handleOpenChange(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (isDocked) {
    return <div className={cn("h-full shrink-0 min-w-0 overflow-hidden bg-white", chatPanelWidthClasses)}>{children}</div>;
  }

  if (isOpen) hasOpened.current = true;
  const shouldRenderOverlay = eagerMount || hasOpened.current;

  return (
    <>
      <ChatFab hasUnread={hasUnread} onClick={() => handleOpenChange(true)} />

      <div
        aria-hidden="true"
        onClick={() => handleOpenChange(false)}
        className={cn(
          "fixed inset-0 z-40 bg-black/70 transition-opacity duration-300",
          isOpen ? "opacity-100" : "pointer-events-none opacity-0"
        )}
      />

      {shouldRenderOverlay && (
        <div className="fixed inset-0 z-50 overflow-clip pointer-events-none">
          <div
            role="dialog"
            aria-modal={isOpen}
            aria-label="Chat"
            aria-hidden={!isOpen}
            data-testid="chat-overlay"
            className={cn(
              "absolute inset-y-0 right-0 w-[405px] max-w-full bg-white shadow-lg transition-transform duration-300 ease-in-out",
              isOpen ? "translate-x-0 pointer-events-auto" : "translate-x-full pointer-events-none"
            )}
          >
            <div className="relative h-full">
              <button
                type="button"
                aria-label="Close chat"
                onClick={() => handleOpenChange(false)}
                className="absolute top-3 right-3 z-10 size-8 flex items-center justify-center rounded-lg hover:bg-innoveraZinc-100 transition-colors"
              >
                <X className="size-4 text-innoveraZinc-900" />
              </button>
              {children}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
